import React, { useEffect, useState, useRef } from 'react';
import { styles } from "../../../../utils/styles";
import { CategoryIcon } from 'assests/GmsIcons';
import { getProductsByCategory } from '../../../../services/ApiHelper';
import CategoryVerticalCard from './CategoryVerticalCard';
import CategoryHorizontalCard from './CategoryHorizontalCard';
import { SortConfig, SortableAttribute } from '../../../../types/sortconfig.types';

type CategoryComponentProps = {
  direction?: 'vertical' | 'horizontal';
  title?: string;
  description?: string;
  showInStockOnly?: boolean;
  onShowInStockChange?: (checked: boolean) => void;
  sortOptions?: string[];
  selectedSort?: string;
  onSortChange?: (option: string) => void;
  
  
  id?: string;
  categoryId?: string;
  industryId?: string;
  
  // Category Details Configuration
  label?: string;
  showTitle?: boolean;
  titleText?: string;
  showCategoryDescription?: boolean;
  descriptionText?: string;
  showThumbnail?: boolean;
  imageDisplay?: 'Fit' | 'Fill' | 'Stretch';
  categoryImageUrl?: string;
  showSortByAttributes?: boolean;
  sortConfig?: SortConfig;
  showInStock?: boolean;
  inStockText?: string;
  selectByDefault?: boolean;
  hideIfNoProducts?: boolean;
  
  // Product Listing Configuration
  style?: 'Grid' | 'List';
  productsPerRow?: string;
  productsLoading?: 'Infinite Scroll' | 'Load More Button';
  scrollBarAfterRows?: string;
  showButtonAfterProducts?: string;
  scrollBarAfterProducts?: string;
  
  // Product Details Configuration
  showModelNumber?: boolean;
  showProductDescription?: boolean;
  descriptionStyle?: string;
  showPrice?: boolean;
  showAvailabilityStatus?: boolean;
  inStockLabel?: string;
  backOrderLabel?: string;
  showVariants?: boolean;
  showNumberOfVariants?: boolean;
  showCategory?: boolean;
  showManufacturer?: boolean;
  productDetailsTextAlignment?: 'Left' | 'Centre' | 'Right';
  showButtonText?: boolean;
  buttonLabel?: string;
  showButtonIcon?: boolean;
  buttonIconUrl?: string;
  buttonPlacement?: 'Left' | 'Centre' | 'Right';

  isSelected?: boolean;
  isEmpty?: boolean; 
}

interface ProductData {
  id: string;
  name: string;
  description: string;
  min_ad_price: number;
  category: string;
  Brand?: string;
  model_number?: string;
  upc?: string;
  version?: string;
  [key: string]: any;
}


export type ProductCardProps = {
  product: ProductData;
  buttonLabel?: string;
  showPrice?: boolean;
  showAvailabilityStatus?: boolean;
  inStockLabel?: string;
  backOrderLabel?: string;
  showProductDescription?: boolean,
  descriptionStyle?: string,
  showCategory?: boolean,
  showModelNumber?: boolean,
  showButtonIcon?: boolean,
  buttonIconUrl?: string,
  buttonPlacement?: 'Left' | 'Centre' | 'Right',
}

const CategoryComponent = ({
  direction = 'vertical',
  title = 'Select AC Motors',
  description = 'Compatibility based on selections in your build',
  showInStockOnly = false,
  onShowInStockChange = () => {},
  sortOptions = ['Attributes'],
  selectedSort = 'Attributes',
  onSortChange = () => {},

  id = '',
  categoryId = '',
  industryId = '',
  isEmpty = false,
  
  label,
  showTitle = true,
  titleText,
  showCategoryDescription = true,
  descriptionText,
  showThumbnail = true,
  imageDisplay = 'Fit',
  categoryImageUrl = '',
  showSortByAttributes = true,
  sortConfig,
  showInStock = true,
  inStockText = 'Show In Stock only',
  selectByDefault = false,
  hideIfNoProducts = false,
  
  // Product Listing
  style = 'Grid',
  productsPerRow = '2',
  productsLoading = 'Infinite Scroll',
  scrollBarAfterRows = '4',
  showButtonAfterProducts = '20',
  scrollBarAfterProducts = '20',
  
  // Product Details
  showModelNumber = false,
  showProductDescription = true,
  descriptionStyle = 'Full',
  showPrice = true,
  showAvailabilityStatus = true,
  inStockLabel = 'In Stock',
  backOrderLabel = 'Out of Stock',
  showVariants = false,
  showNumberOfVariants = false,
  showCategory = false,
  showManufacturer = false,
  productDetailsTextAlignment = 'Left',
  showButtonText = true,
  buttonLabel = 'Add to Build',
  showButtonIcon = false,
  buttonIconUrl = '',
  buttonPlacement = 'Right',

  isSelected = false
}: CategoryComponentProps) => {
  const baseId = id || 'category-component';

  const displayTitle = showTitle ? (titleText || title || 'Select Category') : '';
  const displayDescription = showCategoryDescription ? (descriptionText || description || '') : '';
  const displayInStockText = inStockText || 'Show In Stock only';
  const displayButtonLabel = buttonLabel || 'Add to Build';
  const canFetchProducts = categoryId && industryId && !isEmpty;
  
  const [products, setProducts] = useState<ProductData[]>([]);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInStockChecked, setIsInStockChecked] = useState(selectByDefault);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLElement>(null);

  
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId: NodeJS.Timeout;

    const resizeObserver = new ResizeObserver((entries) => {
      
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      }, 0);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const isNarrowContainer = containerWidth > 0 && containerWidth < 250;
  const isMediumContainer = containerWidth >= 250 && containerWidth < 450;

  useEffect(() => {
    if (canFetchProducts) {
      fetchProducts();
    } else if (isEmpty) {
      console.warn(`Category wrapper (ID: ${id}) has no actual category ID. Cannot fetch products.`);
      setProducts([]);
      setAttributes([]);
    }
  }, [categoryId, industryId, isEmpty]);

  useEffect(() => {
    setIsInStockChecked(selectByDefault);
  }, [selectByDefault]);

  const generateSortOptions = (): string[] => {
    
    if (!sortConfig) {
      return sortOptions || ['Attributes'];
    }

    const options: string[] = [];
    
    if (sortConfig.defaultSortOptions && sortConfig.defaultSortOptions.length > 0) {
      sortConfig.defaultSortOptions.forEach(option => {
        options.push(option.label);
      });
    }
    
    if (sortConfig.sortableAttributes && sortConfig.sortableAttributes.length > 0) {
      sortConfig.sortableAttributes.forEach(attr => {
        const attributeLabel = (attr.label && attr.label.trim()) ? attr.label : attr.name;
        
        if (attributeLabel && attributeLabel.trim() && !options.includes(attributeLabel)) {
          options.push(attributeLabel);
        }
      });
    }
    
    return options;
  };


  const displaySortOptions = sortConfig ? generateSortOptions() : (sortOptions || ['Attributes']);

  const fetchProducts = async () => {
    if (!categoryId) {
      console.warn('No category ID available for fetching products');
      setProducts([]);
      setError('No category selected');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getProductsByCategory(industryId, categoryId);
      
      if (response?.done && response?.data) {
        setProducts(response.data.products || []);
        setAttributes(response.data.attributes || []);
      } else {
        setError('Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Error loading products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const defaultProducts = [
    { id: '1', name: 'Product Name', description: 'Product Description', min_ad_price: 0, category: '', Brand: '' },
    { id: '2', name: 'Product Name', description: 'Product Description', min_ad_price: 0, category: '', Brand: '' },
  ];

  const displayProducts = products.length > 0 ? products : defaultProducts;

  return (
    <section ref={containerRef} id={baseId} data-testid={baseId} className="tw-mb-4 tw-p-2 tw-w-full">
      <header id={`${baseId}-header`} className="tw-flex tw-items-start tw-w-full">
        <span id={`${baseId}-icon`} className="tw-flex-shrink-0 tw-mt-1">
          <CategoryIcon />
        </span>

        <div id={`${baseId}-content`} className="tw-flex-1 tw-ml-2 tw-min-w-0">
          {/* Header with image, title, and stock filter */}
          <div id={`${baseId}-header-section`} className={`tw-w-full tw-mb-2 ${isNarrowContainer ? 'tw-flex tw-flex-col tw-gap-2' : 'tw-flex tw-items-start'}`}>
            {/* Show thumbnail based on configuration */}
            {showThumbnail && !isNarrowContainer && (
              <div id={`${baseId}-thumbnail`} className="tw-w-[68px] tw-h-[68px] tw-rounded-md tw-border tw-border-[#969fae] tw-flex-shrink-0 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-bg-[#e6e7e9]">
                {categoryImageUrl ? (
                  <img
                    src={categoryImageUrl}
                    alt="Category thumbnail"
                    className={`tw-w-full tw-h-full ${
                      imageDisplay === 'Fit' ? 'tw-object-contain' :
                      imageDisplay === 'Fill' ? 'tw-object-cover' :
                      'tw-object-stretch'
                    }`}
                    onError={(e) => {
                      console.error('Thumbnail image load error:', categoryImageUrl);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : null}
              </div>
            )}
            {showThumbnail && isNarrowContainer && (
              <div id={`${baseId}-thumbnail`} className="tw-w-10 tw-h-10 tw-rounded-md tw-border tw-border-[#969fae] tw-flex-shrink-0 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-bg-[#e6e7e9]">
                {categoryImageUrl ? (
                  <img
                    src={categoryImageUrl}
                    alt="Category thumbnail"
                    className={`tw-w-full tw-h-full ${
                      imageDisplay === 'Fit' ? 'tw-object-contain' :
                      imageDisplay === 'Fill' ? 'tw-object-cover' :
                      'tw-object-stretch'
                    }`}
                    onError={(e) => {
                      console.error('Thumbnail image load error:', categoryImageUrl);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : null}
              </div>
            )}

            <div id={`${baseId}-text-section`} className={`tw-flex-1 tw-min-w-0 ${isNarrowContainer ? '' : 'tw-mx-2'}`}>
              {/* Show title based on configuration */}
              {showTitle && (
                <h3 id={`${baseId}-title`} className={`tw-text-[#172b4d] tw-font-medium tw-leading-tight ${isNarrowContainer ? 'tw-text-xs' : 'tw-text-sm tw-truncate'}`}>{displayTitle}</h3>
              )}
              {/* Show description based on configuration */}
              {showCategoryDescription && (
                <div
                  id={`${baseId}-description`}
                  className={`tw-text-[#172b4d] tw-font-normal tw-leading-tight tw-mt-1 ${isNarrowContainer ? 'tw-text-[8px]' : 'tw-text-[10px]'}`}
                  dangerouslySetInnerHTML={{ __html: displayDescription }}
                />
              )}
            </div>

            {/* Show in stock filter - only visible when showInStock is true */}
              {showInStock ? <label id={`${baseId}-stock-filter`} className={`tw-flex tw-items-center tw-flex-shrink-0 ${isNarrowContainer ? 'tw-ml-0' : 'tw-ml-2'}`}>
                <input
                  id={`${baseId}-stock-checkbox`}
                  data-testid={`${baseId}-stock-checkbox`}
                  type="checkbox"
                  checked={selectByDefault}
                  onChange={(e) => {
                    setIsInStockChecked(e.target.checked);
                    onShowInStockChange(e.target.checked);
                  }}
                  className={`${styles.checkboxInput} tw-h-4 tw-mr-1`}
                />
                <span id={`${baseId}-stock-label`} className={`tw-text-[#172b4d] tw-font-medium tw-leading-tight ${isNarrowContainer ? 'tw-text-[8px]' : 'tw-text-[10px] tw-whitespace-nowrap'}`}>
                  {displayInStockText}
                </span>
              </label> : null}

          </div>
          
          {/* Sort controls */}
          {showSortByAttributes && (
            <div id={`${baseId}-sort-section`} className={`tw-mb-6 ${isNarrowContainer ? 'tw-flex tw-flex-col tw-gap-1' : 'tw-flex tw-items-center'}`}>
              {!isNarrowContainer && (
                <span id={`${baseId}-sort-label`} className="tw-text-[#172b4d] tw-text-[10px] tw-font-medium tw-leading-tight tw-mr-3">
                  Sort by:
                </span>
              )}
              <div id={`${baseId}-sort-wrapper`} className={isNarrowContainer ? 'tw-w-full' : 'tw-w-[118px]'}>
                <select
                  id={`${baseId}-sort-select`}
                  data-testid={`${baseId}-sort-select`}
                  value={selectedSort}
                  onChange={(e) => onSortChange(e.target.value)}
                  className={`tw-w-full tw-text-[#6d788c] tw-font-medium tw-border tw-border-[#DADBDD] tw-rounded tw-px-2 tw-py-1.5 ${isNarrowContainer ? 'tw-text-[8px]' : 'tw-text-[10px]'}`}
                >
                  {displaySortOptions.map((option, index) => {
                    return (
                      <option key={`${option}-${index}`} id={`${baseId}-sort-option-${index}`} value={option} className={`tw-text-[#6d788c] tw-font-medium ${isNarrowContainer ? 'tw-text-[8px]' : 'tw-text-[10px]'}`}>
                        {option}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          {loading && (
            <div id={`${baseId}-loading`} className="tw-text-[#6d788c] tw-text-sm tw-py-4 tw-text-center">
              Loading products...
            </div>
          )}

          {error && (
            <div id={`${baseId}-error`} className="tw-text-red-500 tw-text-sm tw-py-4 tw-text-center">
              {error}
            </div>
          )}
          
          {!loading && !error && (
            <>
              {style === 'List' ? (
                <div id={`${baseId}-products-list`} className="tw-grid tw-grid-cols-1 tw-gap-2">
                  {displayProducts.slice(0, parseInt(productsPerRow) || 2).map((product, index) => (
                    <CategoryHorizontalCard
                      id={`${baseId}-product-card-${index}`}
                      key={product.id || index}
                      product={product}
                      buttonLabel={displayButtonLabel}
                      showButtonText={showButtonText}
                      showPrice={showPrice}
                      showAvailabilityStatus={showAvailabilityStatus}
                      inStockLabel={inStockLabel}
                      backOrderLabel={backOrderLabel}
                      showProductDescription={showProductDescription}
                      descriptionStyle={descriptionStyle}
                      showCategory={showCategory}
                      showModelNumber={showModelNumber}
                      showButtonIcon={showButtonIcon}
                      buttonIconUrl={buttonIconUrl}
                      buttonPlacement={buttonPlacement}
                      isNarrowContainer={isNarrowContainer}
                      isMediumContainer={isMediumContainer}
                    />
                  ))}
                </div>
              ) : (
                <div id={`${baseId}-products-grid`} className={`tw-flex tw-gap-4 ${isNarrowContainer ? 'tw-flex-col' : 'tw-flex-row tw-overflow-x-auto'}`}>
                  {displayProducts.slice(0, isNarrowContainer ? 1 : (parseInt(productsPerRow) || 2)).map((product, index) => (
                    <CategoryVerticalCard
                      id={`${baseId}-product-card-${index}`}
                      key={product.id || index}
                      product={product}
                      buttonLabel={displayButtonLabel}
                      showButtonText={showButtonText}
                      showPrice={showPrice}
                      showAvailabilityStatus={showAvailabilityStatus}
                      inStockLabel={inStockLabel}
                      backOrderLabel={backOrderLabel}
                      showProductDescription={showProductDescription}
                      descriptionStyle={descriptionStyle}
                      showCategory={showCategory}
                      showModelNumber={showModelNumber}
                      showButtonIcon={showButtonIcon}
                      buttonIconUrl={buttonIconUrl}
                      buttonPlacement={buttonPlacement}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </header>
    </section>
  );
};

export default CategoryComponent;